import React, {useState} from 'react';
import * as Yup from 'yup';
import {Avatar, Button, Container, createStyles, Group, Menu, Modal, Stack, Text,} from '@mantine/core';
import {ChevronDown, EyeOff, Logout, Settings} from 'tabler-icons-react';
import {Link} from "react-router-dom";
import {CustomInputPassword} from '../hooks/CustomInput';
import {showNotification} from '@mantine/notifications';
import {useForm, yupResolver} from '@mantine/form';
import {Profile} from "../types/allTypes";
import {UseActions} from "../store";
import {useSpeakerMutation} from "../store/slice/customApiSlice";

const useStyles = createStyles((theme) => ({
  header: {
    paddingTop: theme.spacing.sm,
    background: "linear-gradient(to bottom, #b02828 0%, #3c1717 100%)",
    opacity: "0.9"
  },

  mainSection: {
    padding: 0,
    paddingBottom: theme.spacing.sm,
  },

  userMenu: {
    [theme.fn.smallerThan('xs')]: {
      display: 'none',
    },
  },

  user: {
    color: theme.white,
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    borderRadius: theme.radius.sm,
    transition: 'background-color 100ms ease',

    '&:hover': {
      backgroundColor: theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 7 : 5],
    },
  },

  userActive: {
    backgroundColor: theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 7 : 5],
  },

}));

interface HeaderTabsProps {
  profile?: Profile;
}

type PasswordPayload = {
  newPassword: string
  oldPassword: string
}
const passSchema = Yup.object().shape({
  newPassword: Yup.string().required('Введите пароль').min(6, 'Минимальная длина пароля 6 символов'),
  oldPassword: Yup.string().required('Введите текущий пароль').min(6, 'Минимальная длина пароля 6 символов'),
  confirmPassword: Yup.mixed().oneOf([Yup.ref('newPassword'), null], 'Пароли не совпадают'),
});

export function Header({profile}: HeaderTabsProps) {
  const [openPass, setOpenPass] = useState(false);
  const {classes, theme, cx} = useStyles();
  const [userMenuOpened, setUserMenuOpened] = useState(false);
  const {
    logout
  } = UseActions();
  const [request] = useSpeakerMutation();

  const form = useForm({
    initialValues: {
      newPassword: '',
      oldPassword: '',
      confirmPassword: '',
    },
    validate: yupResolver(passSchema),
  });
  const signOutUser = () => {
    const body = {
      inn: profile?.inn || "",
      refreshToken: localStorage.getItem("gra:refresh-token") || "",
    };
    //TODO: add request to server
    logout();
  };

  const changePasswordFunc = async (values: PasswordPayload) => {


    const body = {
      newPassword: values.newPassword,
      oldPassword: values.oldPassword,
    }

    try {
      await request({
        path: `/users/change-password`,
        body,
        method: 'PUT'
      });
      showNotification({
        title: 'Успешно',
        message: 'Пароль успешно изменен',
        color: theme.colors.green[7],
        icon: <EyeOff size={20}/>,
      });
      setOpenPass(false);
    } catch (error: any) {
      showNotification({
        title: 'Ошибка',
        message: `${error.message}`,
        color: theme.colors.red[9],
        icon: <EyeOff/>,
      })
    }
  }

  return (
    <div className={classes.header}>
      <Container sx={{
        minWidth: '100%',
      }} className={classes.mainSection}>
        <Group position="apart">
          <Link style={{
            textDecoration: 'none',
          }} to='/'>
            <Group position='apart' sx={{padding: '0px 12px'}}>
              <img src="./logo.png" style={{width: '2.5rem'}} alt='logo'/>
              <Text sx={{color: 'white', fontSize: '1.2rem'}}>
                Личный кабинет адвоката
              </Text>
            </Group>
          </Link>
          <Menu
            withArrow
            width={250}
            position="bottom-end"
            transitionDuration={150}
            onClose={() => setUserMenuOpened(false)}
            onOpen={() => setUserMenuOpened(true)}
          >
            <Menu.Target>
              <Button sx={{
                background: "none", ":hover": {
                  background: "none", color: "#f7f4eb",
                }
              }}>
                <Group spacing={7} sx={{
                  background: "none", ":hover": {
                    background: "none", color: "#f7f4eb",
                  }
                }} className={cx(classes.user, {[classes.userActive]: userMenuOpened})}>
                  <Avatar src={profile?.photo} alt={profile?.name} radius="xl" size={28}/>
                  <Text weight={500} size="sm" sx={{lineHeight: 1, color: theme.white}} mr={3}>
                    {profile?.name}
                  </Text>
                  <ChevronDown size={12}/>
                </Group>
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Настройки</Menu.Label>
              <Menu.Item component={Link} to="/profile" icon={<Settings size={14}/>}>Профиль</Menu.Item>
              <Menu.Item onClick={() => setOpenPass(true)} icon={<EyeOff size={14}/>}>Сменить пароль</Menu.Item>
              <Menu.Divider/>
              <Menu.Item onClick={signOutUser} icon={<Logout size={14}/>}>Выход</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Container>
      <Modal
        opened={openPass}
        centered
        overlayOpacity={0.55}
        overlayBlur={3}
        onClose={() => setOpenPass(false)}
        size="lg"
        title="Смена пароля"
        transition="fade"
        transitionDuration={500}
        transitionTimingFunction="ease"
      >
        <form onSubmit={form.onSubmit((values) => changePasswordFunc(values))}>
          <Stack sx={{maxWidth: 380}} mx="auto">
            <CustomInputPassword
              label="Текущий пароль"
              required
              {...form.getInputProps('oldPassword')}
            />
            <CustomInputPassword
              label="Новый пароль"
              required
              {...form.getInputProps('newPassword')}
            />
            <CustomInputPassword
              label="Повторите новый пароль"
              required
              {...form.getInputProps('confirmPassword')}
            />
            <Button
              type='submit'
              variant='gradient'
              color='dark'
              sx={{marginBottom: '1rem'}}
            >
              Сменить пароль
            </Button>
          </Stack>
        </form>
      </Modal>
    </div>
  );
}